import { SimpleGrid } from "@mantine/core";
import { useEffect, useMemo, useRef } from "react";

import { useLogger } from "@/sdk";

import { CustomerRatingCard } from "../cards/CustomerRatingCard";
import { SalesCard } from "../cards/SalesCard";
import { TransferRateCard } from "../cards/TransferRateCard";
import { usePayMetrics } from "../data/usePayMetrics";
import { getMetricValue } from "../data/utils";
import { useHubPanelStore } from "../right-panel/useHubPanelStore";

export function YesterdayMetrics() {
    const { payMetrics, isLoading } = usePayMetrics();
    const togglePanel = useHubPanelStore((s) => s.togglePanel);
    const logger = useLogger({ module: "YesterdayMetrics" });
    const hasCheckedMetrics = useRef(false);

    const { serveCalc, serveMetricType, solveCalc, sellCalc, hasTransferRateSupporting } = useMemo(() => {
        const totals = payMetrics?.totals;
        const erpCalc = totals ? getMetricValue(totals.serve, "ERP") : null;
        const expertStarCalc = totals ? getMetricValue(totals.serve, "Expert 5Star") : null;
        return {
            serveCalc: erpCalc ?? expertStarCalc,
            serveMetricType: erpCalc !== null ? ("entireRepPerformance" as const) : ("fiveStarRating" as const),
            solveCalc: totals ? getMetricValue(totals.solve, "Transfer Rate") : null,
            sellCalc: totals ? getMetricValue(totals.sell, "Net Enrolls") : null,
            hasTransferRateSupporting: totals != null && (totals.solve["OCR"] != null || totals.serve["CRT"] != null),
        };
    }, [payMetrics]);

    useEffect(() => {
        if (isLoading || hasCheckedMetrics.current) return;
        hasCheckedMetrics.current = true;
        if (serveCalc == null && solveCalc == null && sellCalc == null) {
            logger.debug({ serveCalc, solveCalc, sellCalc }, "pay-metrics API returned no data for yesterday's stats");
        }
    }, [isLoading, logger, serveCalc, solveCalc, sellCalc]);

    return (
        <SimpleGrid type="container" cols={{ base: 1, "440px": 2, "680px": 3 }} spacing="md">
            <CustomerRatingCard view={{ recentRating: serveCalc, isLoading }} metricType={serveMetricType} />
            <TransferRateCard
                view={{ transferRate: solveCalc, isLoading }}
                onClick={hasTransferRateSupporting ? () => togglePanel("transfer-rate") : undefined}
            />
            <SalesCard view={{ sales: sellCalc, isLoading }} />
        </SimpleGrid>
    );
}
